import {
  previewBootstrap,
  singleLocationPreviewBootstrap,
} from "@shared/fixture";
import type {
  BookingConfirmation,
  CompletionResult,
  DraftSaveResult,
  FormFeedback,
  OnboardingAnswers,
  OnboardingBootstrap,
  SaveDraftPayload,
  VersionedSaveDraftPayload,
  SchedulingAvailability,
} from "@shared/types";

const PREVIEW_STORAGE_KEY = "hvac-launch:onboarding-preview";

function activePreviewBootstrap(): OnboardingBootstrap {
  return new URLSearchParams(window.location.search).get("scenario") === "single"
    ? singleLocationPreviewBootstrap
    : previewBootstrap;
}

function confirmationPreviewBootstrap(): OnboardingBootstrap {
  const bootstrap = activePreviewBootstrap();
  const startTime = new Date();
  startTime.setDate(startTime.getDate() + 3);
  startTime.setHours(10, 0, 0, 0);
  return {
    ...bootstrap,
    answers: {
      ...bootstrap.answers,
      submitted: true,
      meetingBooked: true,
    },
    currentStep: "complete",
    status: "completed",
    booking: {
      startTime: startTime.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      rescheduleUrl: "https://calendly.com/",
      cancelUrl: "https://calendly.com/",
    },
  };
}

function previewStorageKey(): string {
  return new URLSearchParams(window.location.search).get("scenario") === "single"
    ? `${PREVIEW_STORAGE_KEY}:single`
    : PREVIEW_STORAGE_KEY;
}

export function inviteTokenFromPath(): string | null {
  const match = window.location.pathname.match(/^\/i\/([^/]+)\/?$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export async function loadOnboarding(): Promise<OnboardingBootstrap> {
  const token = inviteTokenFromPath();
  if (!token) {
    if (new URLSearchParams(window.location.search).get("demo") === "confirmation") {
      return confirmationPreviewBootstrap();
    }
    const bootstrap = activePreviewBootstrap();
    const stored = window.localStorage.getItem(previewStorageKey());
    if (!stored) return bootstrap;
    try {
      const parsed = JSON.parse(stored) as {
        answers?: OnboardingAnswers;
        currentStep?: string;
      };
      return {
        ...bootstrap,
        answers: parsed.answers ?? bootstrap.answers,
        currentStep: parsed.currentStep,
        status: parsed.answers?.submitted ? "completed" : "in_progress",
        revision: 0,
      };
    } catch {
      return bootstrap;
    }
  }

  const response = await fetch(`/api/invites/${encodeURIComponent(token)}`, {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? "This kickoff link could not be opened.");
  }
  return await response.json() as OnboardingBootstrap;
}

export class DraftSaveConflictError extends Error {
  readonly name = "DraftSaveConflictError";
  constructor(readonly currentRevision: number) {
    super("A newer copy of this form was saved in another tab.");
  }
}

export async function saveDraft(
  payload: VersionedSaveDraftPayload,
  options: { keepalive?: boolean } = {},
): Promise<DraftSaveResult> {
  const token = inviteTokenFromPath();
  if (!token) {
    window.localStorage.setItem(previewStorageKey(), JSON.stringify(payload));
    return {
      revision: payload.expectedRevision + 1,
      updatedAt: new Date().toISOString(),
    };
  }

  const response = await fetch(
    `/api/invites/${encodeURIComponent(token)}/draft`,
    {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: options.keepalive,
    },
  );
  const body = await response.json().catch(() => ({})) as {
    revision?: number;
    updatedAt?: string;
    error?: string;
    code?: string;
    currentRevision?: number;
  };
  if (response.status === 409 && body.code === "revision_conflict") {
    throw new DraftSaveConflictError(body.currentRevision ?? payload.expectedRevision);
  }
  if (!response.ok || body.revision === undefined || !body.updatedAt) {
    throw new Error(body.error ?? "Your latest answers could not be saved.");
  }
  return { revision: body.revision, updatedAt: body.updatedAt };
}

export async function completeOnboarding(
  answers: OnboardingAnswers,
  version: { expectedRevision: number; mutationId: string },
): Promise<CompletionResult> {
  const token = inviteTokenFromPath();
  if (!token) {
    window.localStorage.setItem(
      previewStorageKey(),
      JSON.stringify({ answers, currentStep: "scheduler" }),
    );
    return {
      schedulerUrl: activePreviewBootstrap().client.schedulerUrl,
      revision: version.expectedRevision + 1,
      updatedAt: new Date().toISOString(),
    };
  }

  const response = await fetch(
    `/api/invites/${encodeURIComponent(token)}/complete`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers, ...version }),
    },
  );
  const body = await response.json().catch(() => ({})) as {
    error?: string;
    schedulerUrl?: string;
    revision?: number;
    updatedAt?: string;
    code?: string;
    currentRevision?: number;
  };
  if (response.status === 409 && body.code === "revision_conflict") {
    throw new DraftSaveConflictError(body.currentRevision ?? version.expectedRevision);
  }
  if (!response.ok) {
    throw new Error(body.error ?? "We could not submit your kickoff form.");
  }
  if (body.revision === undefined || !body.updatedAt) {
    throw new Error("The server did not confirm the saved submission version.");
  }
  return body as CompletionResult;
}

export async function loadSchedulingAvailability(
  timezone: string,
): Promise<SchedulingAvailability> {
  const token = inviteTokenFromPath();
  if (!token) {
    const slots: SchedulingAvailability["slots"] = [];
    const day = new Date();
    while (new Set(slots.map((slot) => slot.startTime.slice(0, 10))).size < 4) {
      day.setDate(day.getDate() + 1);
      if (day.getDay() === 0 || day.getDay() === 6) continue;
      for (const [hour, minute] of [[9, 0], [10, 30], [13, 0], [14, 30]]) {
        const slot = new Date(day);
        slot.setHours(hour, minute, 0, 0);
        slots.push({ startTime: slot.toISOString(), status: "available" });
      }
    }
    return { slots, timezone, preview: true };
  }

  const url = new URL(
    `/api/invites/${encodeURIComponent(token)}/scheduling/availability`,
    window.location.origin,
  );
  url.searchParams.set("timezone", timezone);
  const response = await fetch(url, {
    credentials: "same-origin",
    cache: "no-store",
  });
  const body = await response.json().catch(() => ({})) as
    Partial<SchedulingAvailability> & { error?: string };
  if (!response.ok) {
    throw new Error(
      body.error ?? "Available meeting times could not be loaded.",
    );
  }
  return body as SchedulingAvailability;
}

export async function bookOnboardingMeeting(
  startTime: string,
  timezone: string,
): Promise<BookingConfirmation> {
  const token = inviteTokenFromPath();
  if (!token) return { startTime, timezone };

  const response = await fetch(
    `/api/invites/${encodeURIComponent(token)}/scheduling/book`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ startTime, timezone }),
    },
  );
  const body = await response.json().catch(() => ({})) as {
    booking?: BookingConfirmation;
    error?: string;
  };
  if (!response.ok || !body.booking) {
    throw new Error(body.error ?? "This meeting could not be booked.");
  }
  return body.booking;
}

export async function submitFormFeedback(
  feedback: FormFeedback,
): Promise<void> {
  const token = inviteTokenFromPath();
  if (!token) return;

  const response = await fetch(
    `/api/invites/${encodeURIComponent(token)}/feedback`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(feedback),
    },
  );
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) {
    throw new Error(body.error ?? "Your feedback could not be sent.");
  }
}

export function resetPreview(): void {
  window.localStorage.removeItem(previewStorageKey());
  window.location.reload();
}
